import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { materialReceiptApi, type MaterialReceipt } from '../api/execution'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { ScopeChip } from '../components/StatusChip'
import { TemplateBar } from '../components/TemplateBar'
import { DayEntryShell } from './entry/DayEntryShell'
import { MaterialMultiForm } from './report/MaterialMultiForm'

/** Direct route to material receipt entry: pick a site and date, record the delivery. */
export function MaterialEntryPage() {
  return (
    <DayEntryShell
      title="Material received"
      subtitle="Record deliveries at site: challan, vehicle, and every material on it."
    >
      {(report, editable) => <MaterialEntryBody reportId={report.id} editable={editable} />}
    </DayEntryShell>
  )
}

function MaterialEntryBody({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = materialReceiptApi.useList(reportId)
  const create = materialReceiptApi.useCreate(reportId)
  const update = materialReceiptApi.useUpdate(reportId)
  const remove = materialReceiptApi.useDelete(reportId)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<MaterialReceipt | null>(null)

  const receipts = list.data ?? []
  const dayTotal = receipts.reduce((sum, r) => sum + Number(r.totalAmount ?? 0), 0)
  const ownTotal = receipts
    .filter((r) => r.supplyScope === 'OWN')
    .reduce((sum, r) => sum + Number(r.totalAmount ?? 0), 0)

  return (
    <>
      <TemplateBar module="MATERIAL" reportId={reportId} editable={editable} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Deliveries for this day
        </Typography>
        {editable && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Record delivery
          </Button>
        )}
      </Box>

      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock error={list.error} />}

      {receipts.map((m: MaterialReceipt) => (
        <Paper key={m.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{m.receiptNumber}</Typography>
            <ScopeChip scope={m.supplyScope} party={m.contractorName} />
            <Typography variant="body2">{m.supplierName ?? 'No supplier'}</Typography>
            {m.challanNumber && (
              <Typography variant="body2" color="text.secondary">challan {m.challanNumber}</Typography>
            )}
            {m.vehicleNumber && (
              <Typography variant="body2" color="text.secondary">vehicle {m.vehicleNumber}</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {m.totalAmount != null && (
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ₹ {Number(m.totalAmount).toLocaleString('en-IN')}
              </Typography>
            )}
            {editable && (
              <>
                <IconButton size="small" title="Edit this delivery" onClick={() => setEditing(m)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" title="Remove" onClick={() => remove.mutate(m.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Accepted</TableCell>
                  <TableCell align="right">Rejected</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {m.items.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{`${i.materialCode ?? ''} ${i.materialName ?? ''}`.trim()}</TableCell>
                    <TableCell>{i.uomCode}</TableCell>
                    <TableCell align="right">{i.orderedQuantity}</TableCell>
                    <TableCell align="right">{i.receivedQuantity}</TableCell>
                    <TableCell align="right">{i.acceptedQuantity}</TableCell>
                    <TableCell align="right">
                      {Number(i.rejectedQuantity) > 0
                        ? <strong style={{ color: '#b3261e' }}>{i.rejectedQuantity}</strong>
                        : '0'}
                    </TableCell>
                    <TableCell align="right">{i.balanceQuantity ?? '-'}</TableCell>
                    <TableCell align="right">{i.rate ?? '-'}</TableCell>
                    <TableCell align="right">{i.amount ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {receipts.length === 0 && !list.isLoading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No deliveries recorded for this day.
          </Typography>
        </Paper>
      )}

      {receipts.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {receipts.length} delivery(ies) · total value ₹ {dayTotal.toLocaleString('en-IN')} — own
          procurement ₹ {ownTotal.toLocaleString('en-IN')}, contractor and client supply ₹{' '}
          {(dayTotal - ownTotal).toLocaleString('en-IN')}
        </Alert>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <MaterialMultiForm
          pending={create.isPending}
          error={create.error}
          onCancel={() => setOpen(false)}
          onSave={async (body) => {
            await create.mutateAsync(body)
            setOpen(false)
          }}
        />
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="lg">
        {editing && (
          <MaterialMultiForm
            pending={update.isPending}
            error={update.error}
            initial={{
              receiptNumber: editing.receiptNumber,
              supplierId: editing.supplierId,
              supplyScope: editing.supplyScope,
              contractorId: editing.contractorId,
              challanNumber: editing.challanNumber,
              challanDate: editing.challanDate,
              vehicleNumber: editing.vehicleNumber,
              poNumber: editing.poNumber,
              items: editing.items.map((i) => ({
                materialId: i.materialId,
                orderedQuantity: i.orderedQuantity,
                receivedQuantity: i.receivedQuantity,
                acceptedQuantity: i.acceptedQuantity,
                rejectedQuantity: i.rejectedQuantity,
                rate: i.rate,
              })),
            }}
            onCancel={() => setEditing(null)}
            onSave={async (body) => {
              await update.mutateAsync({ id: editing.id, body })
              setEditing(null)
            }}
          />
        )}
      </Dialog>

      <Box sx={{ height: 24 }} />
    </>
  )
}
